import React, { useState, useEffect, useCallback } from 'react';
import { Board as BoardType, BoardMember } from '../services/api';
import { Board } from './Board';
import { Navbar } from './Navbar';
import { boardApi, boardMemberApi, isBoardAccessDenied, handleBoardAccessDenied } from '../services/api';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Lock } from 'iconoir-react';
import { useAuth } from '../contexts/AuthContext';
import { useBoardChannel } from '../hooks/useBoardChannel';
import {
  applyCardCreated,
  applyCardUpdated,
  applyCardMoved,
  applyCardDeleted,
  removeUserFromAllCards,
  CardCreatedEvent,
  CardUpdatedEvent,
  CardMovedEvent,
  CardDeletedEvent,
} from '../services/cardEvents';
import {
  applyColumnCreated,
  applyColumnUpdated,
  applyColumnDeleted,
  ColumnCreatedEvent,
  ColumnUpdatedEvent,
  ColumnDeletedEvent,
} from '../services/columnEvents';
import { CommentCreatedEvent, CommentUpdatedEvent, CommentDeletedEvent } from '../services/commentEvents';
import { publishCommentEvent } from '../services/commentEventBus';
import {
  applyBoardMemberAdded,
  applyBoardMemberRoleChanged,
  applyBoardMemberRemoved,
  BoardMemberAddedEvent,
  BoardMemberRoleChangedEvent,
  BoardMemberRemovedEvent,
} from '../services/boardMemberEvents';
import { AttachmentAddedEvent, AttachmentDeletedEvent } from '../services/attachmentEvents';
import { CardActivityCreatedEvent } from '../services/cardActivityEvents';
import { publishCardModalEvent } from '../services/cardModalEventBus';

export const BoardPage: React.FC = () => {
  const { user } = useAuth();
  const [boards, setBoards] = useState<BoardType[]>([]);
  const [currentBoard, setCurrentBoard] = useState<BoardType | null>(null);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  // Conserve tous les ids d'invitations acceptées.
  const [acceptedInvitationIds, setAcceptedInvitationIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { boardId: boardIdParam } = useParams<{ boardId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    loadBoards();
  }, [boardIdParam]);

  const loadBoardMembers = async (boardId: number) => {
    try {
      const response = await boardMemberApi.getAll(boardId);
      setBoardMembers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des membres', error);
      if (isBoardAccessDenied(error)) {
        handleBoardAccessDenied();
      }
    }
  };

  useEffect(() => {
    if (currentBoard) {
      loadBoardMembers(currentBoard.id);
    }
  }, [currentBoard?.id]);

  const refreshBoardMembers = () => {
    if (currentBoard) {
      loadBoardMembers(currentBoard.id);
    }
  };

  // Patch local nécessaire car toOthers() n'est pas renvoyé à l'auteur.
  const handleMemberRemovedFromCards = (userId: number) => {
    setCurrentBoard((prev) => (prev ? removeUserFromAllCards(prev, userId) : prev));
  };

  useBoardChannel(currentBoard?.id, {
    onCardCreated: useCallback((event: CardCreatedEvent) => {
      setCurrentBoard((prev) => (prev ? applyCardCreated(prev, event) : prev));
    }, []),
    onCardUpdated: useCallback((event: CardUpdatedEvent) => {
      setCurrentBoard((prev) => (prev ? applyCardUpdated(prev, event) : prev));
    }, []),
    onCardMoved: useCallback((event: CardMovedEvent) => {
      setCurrentBoard((prev) => (prev ? applyCardMoved(prev, event) : prev));
    }, []),
    onCardDeleted: useCallback((event: CardDeletedEvent) => {
      setCurrentBoard((prev) => (prev ? applyCardDeleted(prev, event) : prev));
    }, []),
    onColumnCreated: useCallback((event: ColumnCreatedEvent) => {
      setCurrentBoard((prev) => (prev ? applyColumnCreated(prev, event) : prev));
    }, []),
    onColumnUpdated: useCallback((event: ColumnUpdatedEvent) => {
      setCurrentBoard((prev) => (prev ? applyColumnUpdated(prev, event) : prev));
    }, []),
    onColumnDeleted: useCallback((event: ColumnDeletedEvent) => {
      setCurrentBoard((prev) => (prev ? applyColumnDeleted(prev, event) : prev));
    }, []),
    // Hors currentBoard (state local à CardModal) : relayé via le bus.
    onCommentCreated: useCallback((event: CommentCreatedEvent) => {
      publishCommentEvent({ type: 'created', event });
    }, []),
    onCommentUpdated: useCallback((event: CommentUpdatedEvent) => {
      publishCommentEvent({ type: 'updated', event });
    }, []),
    onCommentDeleted: useCallback((event: CommentDeletedEvent) => {
      publishCommentEvent({ type: 'deleted', event });
    }, []),
    onBoardMemberAdded: useCallback((event: BoardMemberAddedEvent) => {
      setBoardMembers((prev) => (currentBoard ? applyBoardMemberAdded(prev, currentBoard.id, event) : prev));
      if (event.invitation_id !== null) {
        setAcceptedInvitationIds((prev) => [...prev, event.invitation_id as number]);
      }
    }, [currentBoard?.id]),
    onBoardMemberRoleChanged: useCallback((event: BoardMemberRoleChangedEvent) => {
      setBoardMembers((prev) => (currentBoard ? applyBoardMemberRoleChanged(prev, currentBoard.id, event) : prev));
    }, [currentBoard?.id]),
    onBoardMemberRemoved: useCallback((event: BoardMemberRemovedEvent) => {
      if (user && event.user_id === user.id) {
        handleBoardAccessDenied();
        return;
      }
      setBoardMembers((prev) => (currentBoard ? applyBoardMemberRemoved(prev, currentBoard.id, event) : prev));
    }, [currentBoard?.id, user?.id]),
    onAttachmentAdded: useCallback((event: AttachmentAddedEvent) => {
      publishCardModalEvent({ type: 'attachment-added', event });
    }, []),
    onAttachmentDeleted: useCallback((event: AttachmentDeletedEvent) => {
      publishCardModalEvent({ type: 'attachment-deleted', event });
    }, []),
    onCardActivityCreated: useCallback((event: CardActivityCreatedEvent) => {
      publishCardModalEvent({ type: 'activity-created', event });
    }, []),
  });

  const loadBoards = async () => {
    try {
      setLoading(true);
      const response = await boardApi.getAll();
      const boardsData = response.data.boards || [];
      setBoards(boardsData);
      
      // Vérifier si un board spécifique est demandé dans l'URL
      // Priorité 1: boardId depuis le path (/boards/:boardId)
      // Priorité 2: board depuis les query params (/?board=id)
      const boardIdFromPath = boardIdParam ? parseInt(boardIdParam) : null;
      const boardIdFromQuery = searchParams.get('board') ? parseInt(searchParams.get('board')!) : null;
      const requestedBoardId = boardIdFromPath || boardIdFromQuery;
      
      if (requestedBoardId) {
        const requestedBoard = boardsData.find(b => b.id === requestedBoardId);
        if (requestedBoard) {
          setCurrentBoard(requestedBoard);
        } else if (boardsData.length > 0) {
          setCurrentBoard(boardsData[0]);
        }
      } else if (boardsData.length > 0 && !currentBoard) {
        setCurrentBoard(boardsData[0]);
      }
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des tableaux');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentBoard = async () => {
    if (!currentBoard) return;
    
    try {
      const response = await boardApi.getOne(currentBoard.id);
      setCurrentBoard(response.data);
    } catch (err) {
      console.error('Erreur lors du rafraîchissement du tableau', err);
      if (isBoardAccessDenied(err)) {
        handleBoardAccessDenied();
      }
    }
  };

  const handleBoardUpdate = async (updatedBoard: BoardType) => {
    try {
      await boardApi.update(updatedBoard.id, { title: updatedBoard.title });
      await loadBoards();
    } catch (err) {
      setError('Erreur lors de la mise à jour du tableau');
      console.error(err);
    }
  };

  const handleCreateBoard = async () => {
    try {
      const response = await boardApi.create({ title: 'Nouveau Tableau' });
      setBoards([...boards, response.data]);
      setCurrentBoard(response.data);
    } catch (err) {
      setError('Erreur lors de la création du tableau');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-xl">
          <p>{error}</p>
          <button 
            onClick={loadBoards}
            className="mt-4 px-4 py-2 bg-white text-kanban-blue rounded hover:bg-gray-100"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
          <div className="text-center">
            <p className="text-white text-xl mb-4">Aucun tableau disponible</p>
            <button 
              onClick={handleCreateBoard}
              className="px-6 py-3 bg-white text-kanban-blue rounded-lg hover:bg-gray-100 font-semibold"
            >
              Créer un tableau
            </button>
          </div>
        </div>
      </>
    );
  }

  // Tableau verrouillé : ne pas ouvrir, même en accès direct par URL.
  if (currentBoard && currentBoard.locked) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
          <div className="text-center" style={{ maxWidth: 480 }}>
            <Lock width={40} height={40} strokeWidth={2} color="#fff" />
            <p className="text-white text-xl mt-4 mb-4">
              Ce tableau dépasse la limite incluse dans votre offre Premium. Réglez un
              dépassement pour y accéder.
            </p>
            <button
              onClick={() => navigate('/account', { state: { activeTab: 'overages' } })}
              className="px-6 py-3 bg-white text-kanban-blue rounded-lg hover:bg-gray-100 font-semibold"
            >
              Gérer mes dépassements
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="App">
      {currentBoard && (
        <Board
          board={currentBoard}
          boardMembers={boardMembers}
          acceptedInvitationIds={acceptedInvitationIds}
          onMembersRefresh={refreshBoardMembers}
          onMemberRemovedFromCards={handleMemberRemovedFromCards}
          onUpdate={handleBoardUpdate}
          onRefresh={refreshCurrentBoard}
        />
      )}
    </div>
  );
};
